import React, {useState, useEffect} from 'react';
import {Form} from "formik";
import {makeStyles} from "@mui/styles";
import {
    Checkbox, Chip,
    Container,
    FilledInput,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel, ListItem, MenuItem,
    Paper,
    Select,
    Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import PrimaryButton from "../../Common/PrimaryButton";
import {requestApi, showAlertAutoDis} from "../../../utils/utils";
import {v4 as uuidv4} from "uuid";
import SecondaryButton from "../../Common/SecondaryButton";
import {useIsAuthenticated} from "react-auth-kit";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {differenceInDays, parse} from "date-fns";

const useStyles = makeStyles(() => ({
    sectionHeading: {
        fontSize: '2.4rem',
        fontWeight: '600',
        marginBottom: '20px',
    },
    sectionTitle: {
        fontSize: '1.6rem',
        fontWeight: '600',
        marginBottom: '10px',
    },
    formSection: {
        marginBottom: '20px',
    },
    checkboxLabel: {
        fontSize: '1.4rem',
    },
    groupedInput: {
        paddingRight: 0
    },
    gearList: {
        display: 'flex',
        flexWrap: 'wrap',
		gap: '20px'
    },
    gearItem: {
        flex: '0',
		padding: '0'
    },
    gearChip: {
        flex: '0 0 auto',
        fontSize: '1.2rem',
		display: 'flex',
		padding: '10px',
		height: 'auto'
    },
	customLabel: {
		overflow: 'visible',
		whiteSpace: 'unset',
		textOverflow: 'clip',
	},
    diveButlerName: {
        fontSize: '1.4rem',
        fontWeight: '600',
        letterSpacing: '1.2',
        textTransform: 'uppercase',
    },
    diveButlerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    diveButlerLabel: {
        cursor: 'pointer',
        paddingLeft: '16px',
    },
	iconInfo: {
		fontSize: '30px'
	}
}));

const DivingForm = ({values, handleChange, setValues}) => {
    const {search} = useLocation();
    const searchParams = new URLSearchParams(search);
    const crsUser = searchParams.get("crs");
    const classes = useStyles();
    const {form, booking} = useSelector(state => state.bookingForm);
    const [gearList, setGearList] = useState([]);
    const [availableGear, setAvailableGear] = useState([]);
    const [availableSizes, setAvailableSizes] = useState([]);
    const [gearOption, setGearOption] = useState('');
    const [sizeOption, setSizeOption] = useState('');
    const [packageSizeOption, setPackageSizeOption] = useState([]);
    const [selectedGear, setSelectedGear] = useState([]);

	const [isPackageSelected, setIsPackageSelected] = useState(false);
    const [packageGearSelections, setPackageGearSelections] = useState([]);

    const isAuth = useIsAuthenticated();
    const history = useHistory();

    const boardDate = parse(booking.trip.boarddate, 'yyyy-MM-dd', new Date());
    const daysBeforeBoardDate = differenceInDays(boardDate, new Date());
    const disableInputs = daysBeforeBoardDate < 8 && !crsUser;

	 const [open, setOpen] = useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useEffect(() => {
        getRentalOptions().catch();
    }, []);


    useEffect(() => {
        createGearSelectArray();
    }, [gearList]);

    useEffect(() => {
        updateSizeSelectArray();
    }, [gearOption]);

    useEffect(() => {
        updateGearApiSelections();
		// console.log(selectedGear);

    }, [selectedGear]);


	const handleGearChange = (e) => {
        const selectedGear = availableGear.find(g => g.value === e.target.value);
        setGearOption(e.target.value);
        setSizeOption('');
        if (selectedGear?.type === 'package') {
            setIsPackageSelected(true);

            const packageOptions = selectedGear.includes !== null && selectedGear.includes.map((item) => ({
                gearId: item.gearId,
                label: item.gear,
                sizeOption: '',
                sizes: item.sizes,
            }));

            setPackageGearSelections(packageOptions);
        } else {
            setIsPackageSelected(false);
        }
    };

	const handlePackageSizeChange = (gearId, sizeValue) => {
        const updatedSelections = packageGearSelections.map(item =>
            item.gearId === gearId ? {...item, sizeOption: sizeValue} : item
        );
        setPackageSizeOption([...packageSizeOption, {[gearId]: sizeValue}]);
        setPackageGearSelections(updatedSelections);
    };

    const getRentalOptions = async () => {
        try {
            const access = sessionStorage.getItem('accessToken');
            const guestCode = access ? `?guestCode=${access}` : '';
            const {data} = await requestApi(`/guest/get-rentalgear${guestCode}`);
            if (data.status === 'success') {
                setGearList(data.gears);

                await retrieveRentalGearFromDB();
            }
        } catch (e) {

        }
    };

   	const retrieveRentalGearFromDB = async () => {
    const access = sessionStorage.getItem('accessToken');
    const guestCode = access ? `?guestCode=${access}` : '';
    const { data: rentalData } = await requestApi(`/guest/get-rentals/${form.guestFk}${guestCode}`);

    if (rentalData.status === 'success') {
        const rentalsByPackage = {};

        for (let rental of rentalData.rentals) {
            const packageKey = rental.gearTypeId?.startsWith('package_') ? rental.gearTypeId : null;

            const gear = {
                id: uuidv4(),
                value: rental.gearFk,
                label: rental.gearName,
                price: rental.gearPrice,
                selectSize: rental.size !== rental.gearName,
                size: rental.size !== rental.gearName ? rental.size : null,
                sizeId: rental.gearsizeId,
            };

            if (packageKey) {
                if (!rentalsByPackage[packageKey]) {
                    rentalsByPackage[packageKey] = {
                        id: uuidv4(),
                        type: 'package',
                        label: rental.gearName,
                        price: rental.gearPrice,
                        sizeId: rental.gearsizeId,
                        includes: [],
                    };
                }
                rentalsByPackage[packageKey].includes.push(gear);
            } else {
                if (!rentalsByPackage.singleItems) {
                    rentalsByPackage.singleItems = [];
                }
                rentalsByPackage.singleItems.push(gear);
            }
        }

        const rentals = [
            ...Object.values(rentalsByPackage).filter(item => item.type === 'package').map(item => {
                // Filter out the first element in the 'includes' array if it is a package type
                if (item.type === 'package' && item.includes.length > 0) {
                    item.includes = item.includes.slice(1);
                }
                return item;
            }),
            ...(rentalsByPackage.singleItems || []),
        ];

        setSelectedGear(rentals);
        // console.log(rentals);
    }
};


    const createGearSelectArray = () => {
        const gear = [];

        for (let item of gearList) {
            gear.push({
                value: item.gearId,
                label: item.gear,
                type: item.type,
                price: item.price,
                selectSize: item.sizes.length > 1,
                sizes: item.sizes,
				includes: item?.includes || null
            });
        }

        setAvailableGear(gear);
    };

    const updateSizeSelectArray = () => {
        if (!gearOption) return setAvailableSizes([]);
        const currentGear = gearList.find(g => g.gearId === gearOption);
        if (!currentGear || currentGear.sizes.length < 2) return setAvailableSizes([]);

        const sizes = [];
        currentGear.sizes.forEach(option => {
            sizes.push({value: option.gearsizeId, label: option.size});
        });

        setAvailableSizes(sizes);
    };



	const handleAddGear = () => {
		const selectedGearItem = availableGear.find(g => g.value === gearOption);

		if (selectedGearItem?.type === 'package') {
			if (shouldDisablePackageBtn()) {
                showAlertAutoDis({
                    title: 'Error',
                    text: `You must select a size for each gear`,
                });
                return null;
            }


            const newPackage = {
                id: uuidv4(),
                type: 'package',
                label: selectedGearItem.label,
                price: selectedGearItem.price,
                sizeId: selectedGearItem.sizes[0].gearsizeId,
                includes: selectedGearItem.includes.map(item => {
                    const sizeOption = packageSizeOption.find(option => option[item.gearId]);
                    const sizeIdFromOption = sizeOption ? sizeOption[item.gearId] : null;

                    return {
                        id: uuidv4(),
                        value: item.gearId,
                        label: item.gear,
                        price: item.price,
                        selectSize: item.sizes.length > 0,
                        size: sizeIdFromOption ? item.sizes.find(s => s.gearsizeId === sizeIdFromOption)?.size : item.sizes[0].size,
                        sizeId: sizeIdFromOption ? item.sizes.find(s => s.gearsizeId === sizeIdFromOption)?.gearsizeId : item.sizes[0].gearsizeId,
                    };
                })
            };

						// console.log('handleGear package:', newPackage);


            setSelectedGear([...selectedGear, newPackage]);
			setIsPackageSelected(false);
			setPackageSizeOption([])
		} else {
			if (shouldDisableGearBtn()) {
				return showAlertAutoDis({
					title: 'Error',
					text: 'You must select a size for this gear',
				});
			}
			// Добавление обычного элемента (не пакета)
			const id = uuidv4();
			const selectedSize = sizeOption
				? availableSizes.find(s => s.value === sizeOption)
				: (selectedGearItem.sizes && selectedGearItem.sizes.length > 0 ? selectedGearItem.sizes[0] : null);
			const gearToAdd = {
				id,
				value: selectedGearItem.value,
				label: selectedGearItem.label,
				price: selectedGearItem.price,
				selectSize: selectedGearItem.selectSize,
				size: selectedSize ? selectedSize.label : null,
				sizeId: selectedSize ? selectedSize.value : null,
			};
			// console.log('handleGear item:', gearToAdd);
			setSelectedGear([...selectedGear, gearToAdd]);
		}

		setGearOption('');
		setSizeOption('');
	};



    const handleRemoveGear = (id) => {
		const updatedSelection = selectedGear.filter((gear) => gear.id !== id);
		setSelectedGear(updatedSelection);
	};

	const handleRemovePackage = (packageId) => {
		const updatedSelection = selectedGear.filter(
			(gear) => gear.id !== packageId
		);
		setSelectedGear(updatedSelection);
	};

    const shouldDisableSizeSelect = () => {
        if (!gearOption) return true;
        const currentGear = availableGear.find(g => g.value === gearOption);
        return !currentGear || !currentGear.selectSize;
    };

	const shouldDisableSizeSelectForIncludedItem = (gearId) => {
		const includedItem = packageGearSelections.find(item => item.gearId === gearId);
		return includedItem ? includedItem.sizes.length <= 1 : true;
	};

    const shouldDisableGearBtn = () => {
        if (!gearOption) return true;

        const selectedGear = availableGear.find(g => g.value === gearOption);
        return !!(selectedGear.selectSize && !sizeOption);
    };


	const shouldDisablePackageBtn = () => {
		if (!gearOption) return true;

		const selectedGear = availableGear.find(g => g.value === gearOption);
		if (selectedGear?.type !== 'package') return false;

		return selectedGear.includes.some(includedItem =>
			includedItem.sizes.length > 1 && !packageSizeOption.some(option => option[includedItem.gearId])
		);
	};
    const updateGearApiSelections = () => {
    	const rentals = [];
		const seenGearCombination = new Set(); // Множество для отслеживания уникальных сочетаний (gearTypeId + gearSizeId)

		for (let rental of selectedGear) {
			let id = uuidv4();

			if (rental.type === 'package' && rental.includes && rental.includes.length > 0) {
				// Для самого пакета добавляем его только если комбинация gearTypeId + gearSizeId уникальна
				const packageKey = `package_${id}_${rental.sizeId}`;
				if (!seenGearCombination.has(packageKey)) {
					rentals.push({
						gearSizeId: rental.sizeId,
						gearTypeId: `package_${id}`,
						included: true
					});
					seenGearCombination.add(packageKey); // Добавляем уникальное сочетание в Set
				}

				// Для каждого include в пакете добавляем только уникальные сочетания gearTypeId + gearSizeId
				for (let include of rental.includes) {
					const includeKey = `package_${id}_${include.sizeId}`;
					if (!seenGearCombination.has(includeKey)) {
						rentals.push({
							gearSizeId: include.sizeId,
							gearTypeId: `package_${id}`,
							included: true
						});
						seenGearCombination.add(includeKey); // Добавляем уникальное сочетание в Set
					}
				}
			} else {
				// Если rental не является пакетом, то просто добавляем элемент, если комбинация gearTypeId + gearSizeId уникальна
				const defaultSize = rental.sizeId === undefined
					? availableGear.find(item => item.label === rental.label)?.sizes[0]?.gearsizeId
					: rental.sizeId;

				const rentalKey = `default_${defaultSize}`;
				if (!seenGearCombination.has(rentalKey)) {
					rentals.push({
						gearSizeId: defaultSize,
					});
					seenGearCombination.add(rentalKey); // Добавляем уникальное сочетание в Set
				}
			}
		}

		// console.log('updateGearApiSelections:', rentals);

		setValues({
			...values,
			rentals,
		});
	};


    const sortGear = (gearArray) => {
		const typeOrder = { package: 1, item: 2, complimentary: 3, extra: 4 };
		return gearArray.sort((a, b) => (typeOrder[a.type] || 5) - (typeOrder[b.type] || 5));
	};


    return (
        <Form>
            <Grid container sx={{px: {xs: '1.6rem', lg: '0'}}}>

                <Typography variant="h3" className={classes.sectionHeading}>
                    Diving Experience and Information
                </Typography>
                <Grid container spacing={3} className={classes.formSection}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" className={classes.sectionTitle}>Dive Certification Agency</Typography>
                        <FormControl variant="filled" fullWidth>
                            <InputLabel htmlFor="diveCertAgency">Agency</InputLabel>
                            <FilledInput id="diveCertAgency" name="diveCertAgency"
                                         value={values.diveCertAgency}
                                         disabled={disableInputs}
                                         onChange={handleChange} disableUnderline/>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" className={classes.sectionTitle}>Certification Number</Typography>
                        <FormControl variant="filled" fullWidth>
                            <InputLabel htmlFor="diveCertNumber">Number</InputLabel>
                            <FilledInput id="diveCertNumber" name="diveCertNumber"
                                         value={values.diveCertNumber}
                                         disabled={disableInputs}
                                         onChange={handleChange} disableUnderline/>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" className={classes.sectionTitle}>Nitrox Certification Agency</Typography>
                        <FormControl variant="filled" fullWidth>
                            <InputLabel htmlFor="nitroxCertAgency">Agency</InputLabel>
                            <FilledInput id="nitroxCertAgency" name="nitroxCertAgency"
                                         value={values.nitroxCertAgency}
                                         disabled={disableInputs}
                                         onChange={handleChange} disableUnderline/>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" className={classes.sectionTitle}>Certification Number</Typography>
                        <FormControl variant="filled" fullWidth>
                            <InputLabel htmlFor="contact">Number</InputLabel>
                            <FilledInput id="nitroxCertNum" name="nitroxCertNum"
                                         value={values.nitroxCertNum}
                                         disabled={disableInputs}
                                         onChange={handleChange} disableUnderline/>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={12}>
                                <FormControlLabel
                                    control={<Checkbox id="injuryYesNo" name="injuryYesNo" onChange={handleChange}
                                                       disabled={disableInputs}
                                                       checked={values.injuryYesNo}/>}
                                    label="Have you ever had a diving related accident or medical incident?">
                                </FormControlLabel>
                            </Grid>
                            {values.injuryYesNo && <Grid item xs={12} md={12}>
                                <FormControl variant="filled" fullWidth>
                                    <InputLabel htmlFor="injury">Please specify.</InputLabel>
                                    <FilledInput id="injury" name="injury"
                                                 value={values.injury} multiline minRows={3}
                                                 disabled={disableInputs}
                                                 onChange={handleChange} disableUnderline/>
                                </FormControl>
                            </Grid>}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Typography variant="h3" className={classes.sectionHeading}>
                Gear Rental
            </Typography>

            <Paper elevation={3} style={{marginTop: '50px'}} sx={{px: {xs: '20px', lg: '0'}}}>
                <Container style={{padding: '20px 20px 0 20px'}}>
                    <Typography variant="h5" className={classes.sectionTitle}>
                        Add Gear Rental
						<IconButton onClick={handleClickOpen} sx={{ position: 'relative', top: '-2px'}}>
							<InfoIcon color="primary" className={classes.iconInfo}/>
						</IconButton>
                    </Typography>
					<Dialog open={open} onClose={handleClose} aria-labelledby="gear-info-dialog">
						<DialogTitle id="gear-info-dialog" sx={{fontSize: '24px', fontWeight: '700'}}>Gear Rental Information</DialogTitle>
						<DialogContent>
							<DialogContentText>
								To place an order for gear, please follow these steps:
								<br /><br /><strong>Select Gear:</strong> Use the "Gear" dropdown to choose the item you need from the available options. Some gear packages (such as a full gear set) may include multiple items.
								<br /><br /><strong>Select Size:</strong> If the selected gear has multiple size options, an additional "Size" field will appear. Click on this field to choose the appropriate size from the dropdown. If the gear has a fixed size, this field will not be displayed.
								<br /><br /><strong>Add Gear:</strong> After selecting your gear and size (if applicable), click the "Add" button to add the item to your list. If you've selected a package that includes multiple items, please follow the prompts to choose sizes for each included item.
								<br /><br /><strong>Place Order:</strong> Once all desired items and packages are added to your cart, review your selections and click "SEND".
								<br /><br /><strong>Note:</strong> If you need assistance with size selection, please contact our support team – we're here to help!
							</DialogContentText>
					</DialogContent>
					</Dialog>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl variant="filled" fullWidth>
                                <InputLabel id="gearLabel">Gear</InputLabel>
                                <Select
                                    labelId="gearLabel"
                                    id="gear"
                                    name="gear"
                                    label="Gear"
                                    value={gearOption}
                                    onChange={handleGearChange}
                                    disabled={disableInputs}
                                    disableUnderline
									MenuProps={{
										PaperProps: {
											sx: {
												maxWidth: '380px'
											}
										}
									}}
                                >
                                    <MenuItem value={''}>choose gear</MenuItem>
                                    {sortGear(availableGear).map(item => (
                                        <MenuItem
											key={item.value}
											value={item.value}
											sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textTransform:'capitalize'}}>
                                            {item.type && item.type === 'package' ? (<><b>Package</b>&nbsp;</>) : ''}{`${item.label}`} - {!item.price || item.price === "0" ? 'Complimentary' : `$${item.price}`}
										</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {!isPackageSelected && availableSizes.length > 1 && (
                            <Grid item xs={12} md={6}>
                                <FormControl variant="filled" fullWidth>
                                    <InputLabel id="sizeLabel">Size</InputLabel>
                                    <Select
                                        labelId="sizeLabel"
                                        id="size"
                                        name="size"
                                        label="Gear"
                                        value={sizeOption}
                                        onChange={(e) => setSizeOption(e.target.value)}
                                        disableUnderline
                                        disabled={disableInputs || shouldDisableSizeSelect()}
                                    >
                                        <MenuItem value={''}>Select</MenuItem>
                                        {availableSizes.map(size => (
                                            <MenuItem key={size.value} value={size.value}>{size.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {isPackageSelected && (
							packageGearSelections.map(({ gearId, label, sizes, sizeOption }) => (
								sizes.length > 1 && (
									<Grid item xs={12} container spacing={2} key={gearId}>
										<Grid item xs={12} md={6}>
											<FormControl variant="filled" fullWidth>
												<Select value={gearId} disabled>
													<MenuItem value={gearId}>{label}</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} md={6}>
											<FormControl variant="filled" fullWidth>
												<InputLabel>Size</InputLabel>
												<Select
													value={sizeOption}
													onChange={(e) => handlePackageSizeChange(gearId, e.target.value)}
													disableUnderline
													disabled={disableInputs || shouldDisableSizeSelectForIncludedItem(gearId)}
												>
													<MenuItem value={''}>Select Size</MenuItem>
													{sizes.map(size => (
														<MenuItem key={size.gearsizeId} value={size.gearsizeId}>
															{size.size}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
									</Grid>
								)
							))
						)}
                        <Grid item xs={12} md={2}>
                            <PrimaryButton style={{height: '100%', margin: '0'}} disabled={disableInputs} onClick={handleAddGear}>Add</PrimaryButton>
                        </Grid>
                        <Grid item xs={12} className={classes.gearList}>
							{selectedGear.map((gear) => {
								if (gear.type === 'package') {
									// Рендеринг Chip для пакета с вложенными элементами
									return (
										<ListItem key={gear.id} className={classes.gearItem}>
											<Chip
												className={classes.gearChip}
												classes={{ label: classes.customLabel }}
												label={
													<div style={{ display: 'flex', flexDirection: 'column' }}>
														<strong>{gear.label} ({!gear.price || gear.price === "0" ? 'Complimentary' : `$${gear.price}`}):</strong>
														{gear.includes.map((includedItem) => (
															<span key={includedItem.id}>
																{includedItem.label} {includedItem.size ? `(${includedItem.size})` : ''}
															</span>
														))}
													</div>
												}
												onDelete={() => handleRemovePackage(gear.id)}
											/>
										</ListItem>
									);
								}

								// Рендеринг Chip для обычного элемента
								return (
									<ListItem key={gear.id} className={classes.gearItem}>
										<Chip
											className={classes.gearChip}
											classes={{ label: classes.customLabel }}
											label={`${gear.label} ${gear.size ? `(${gear.size})` : ''} - ${!gear.price || gear.price === "0" ? 'Complimentary' : `$${gear.price}`}`}
											onDelete={() => handleRemoveGear(gear.id)}
										/>
									</ListItem>
								);
							})}
						</Grid>

                    </Grid>
                    <Typography variant="h5" sx={{padding:'20px'}} >
                        <br /><strong style={{color:'red'}}>Important:</strong> The rentals you select will not be charged now but onboard at the end of the trip.
                    </Typography>
                </Container>
            </Paper>
            <div style={{marginTop: '50px'}}>
                <Typography variant="h3" className={classes.sectionHeading}>
                    Diving Experience
                </Typography>
                <Typography variant="p">
                    Dive Butler is our answer to allowing experienced and self-reliant divers as much freedom and fun as
                    they want while
                    ensuring that our newer divers are comfortable and well looked after while doing our best to ensure
                    that dive sites are
                    never crowded. We will be splitting the guests into three different groups to ensure each guest has
                    their ideal diving
                    experience. {/*Please select one of the follow groups.*/}
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <div className="diveSelector" style={{display: 'flex', alignItems: 'center', marginTop: '20px'}}>I have &nbsp;
                            <FormControl variant="filled" style={{minWidth: '150px'}}>
                                <Select
                                    labelId="diverDivesLabel"
                                    id="diverDives"
                                    name="diverDives"
                                    label=""
                                    value={values.diverDives}
                                    disabled={disableInputs}
                                    onChange={handleChange}>
                                    <MenuItem value={''}>Select</MenuItem>
                                    <MenuItem value="20">Less than 20</MenuItem>
                                    <MenuItem value="100">Less than 100</MenuItem>
                                    <MenuItem value="500">Less than 500</MenuItem>
                                    <MenuItem value="501">More than 500</MenuItem>
                                </Select>
                            </FormControl>
                            &nbsp; dives
                        </div>

                        <div className="diveSelector" style={{display: 'flex', alignItems: 'center', marginTop: '20px'}}>I have been diving
                            for &nbsp;
                            <FormControl variant="filled" style={{minWidth: '150px'}}>
                                <Select
                                    labelId="diverYearsLabel"
                                    id="diverYears"
                                    name="diverYears"
                                    label=""
                                    value={values.diverYears}
                                    disabled={disableInputs}
                                    onChange={handleChange}>
                                    <MenuItem value={''}>Select</MenuItem>
                                    <MenuItem value="1">Less than 1 year</MenuItem>
                                    <MenuItem value="3">Less than 3 years</MenuItem>
                                    <MenuItem value="4">More than 3 years</MenuItem>
                                </Select>
                            </FormControl>
                            &nbsp;
                        </div>

                        <div className="diveSelector" style={{display: 'flex', alignItems: 'center', marginTop: '20px'}}>My physical
                            fitness is &nbsp;
                            <FormControl variant="filled" style={{minWidth: '150px'}}>
                                <Select
                                    labelId="diverFitnessLabel"
                                    id="diverFitness"
                                    name="diverFitness"
                                    label=""
                                    value={values.diverFitness}
                                    disabled={disableInputs}
                                    onChange={handleChange}>
                                    <MenuItem value={''}>Select</MenuItem>
                                    <MenuItem value="below">Below average</MenuItem>
                                    <MenuItem value="average">Average</MenuItem>
                                    <MenuItem value="above">Above average</MenuItem>
                                    <MenuItem value="fit">Very fit</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                    </Grid>
                </Grid>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '75px', marginBottom: '50px'}}>
                <SecondaryButton onClick={() => history.goBack()} style={{margin: 0}}>Back</SecondaryButton>
                <PrimaryButton style={{margin: 0}} type="submit">{isAuth() ? 'Next' : 'Send'}</PrimaryButton>
            </div>
        </Form>
    );
};

export default DivingForm;